import { useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, Label } from 'recharts';

interface Recommendation {
  id: string;
  title: string;
  category: string;
  priority: number;
  impact: 'high' | 'medium' | 'low';
  effort: 'high' | 'medium' | 'low';
  description: string;
  businessImpact?: string;
}

interface PriorityMatrixProps {
  recommendations: Recommendation[];
  siteName?: string;
}

// Convert impact/effort to numeric values
const impactToNumber = (impact: string): number => {
  switch (impact) {
    case 'high': return 90;
    case 'medium': return 50;
    case 'low': return 20;
    default: return 50;
  }
};

const effortToNumber = (effort: string): number => {
  switch (effort) {
    case 'low': return 20;
    case 'medium': return 50;
    case 'high': return 80;
    default: return 50;
  }
};

// Color by quadrant for clear, actionable meaning
const getQuadrantColor = (x: number, y: number): string => {
  if (x < 50 && y >= 50) return '#22c55e'; // green - Quick Wins
  if (x >= 50 && y >= 50) return '#3b82f6'; // blue - Major Projects
  if (x < 50 && y < 50) return '#6b7280'; // gray - Fill-Ins
  return '#ef4444'; // red - Deprioritize
};

const getQuadrantLabel = (x: number, y: number): string => {
  if (x < 50 && y >= 50) return 'Quick Wins';
  if (x >= 50 && y >= 50) return 'Major Projects';
  if (x < 50 && y < 50) return 'Fill-Ins';
  return 'Deprioritize';
};

export function PriorityMatrix({ recommendations, siteName }: PriorityMatrixProps) {
  const data = useMemo(() => {
    return recommendations.map((rec, index) => ({
      x: effortToNumber(rec.effort) + (Math.random() * 10 - 5), // Add jitter
      y: impactToNumber(rec.impact) + (Math.random() * 10 - 5),
      ...rec,
      index,
    }));
  }, [recommendations]);

  // Count items in each quadrant
  const quadrantCounts = useMemo(() => {
    const counts = { quickWins: 0, majorProjects: 0, fillIns: 0, avoid: 0 };
    data.forEach(item => {
      if (item.x < 50 && item.y >= 50) counts.quickWins++;
      else if (item.x >= 50 && item.y >= 50) counts.majorProjects++;
      else if (item.x < 50 && item.y < 50) counts.fillIns++;
      else counts.avoid++;
    });
    return counts;
  }, [data]);

  if (recommendations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No recommendations to display in matrix.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Priority Matrix
          </h3>
          {siteName && <p className="text-xs text-muted-foreground">{siteName}</p>}
        </div>
        <div className="flex gap-4 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Quick Wins
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500" /> Major Projects
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-500" /> Fill-Ins
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" /> Deprioritize
          </span>
        </div>
      </div>

      {/* Quadrant Summary */}
      <div className="grid grid-cols-4 gap-2 text-center text-xs">
        <div className="bg-green-500/10 border border-green-500/20 rounded p-2">
          <p className="text-green-400 font-medium">{quadrantCounts.quickWins}</p>
          <p className="text-muted-foreground">Quick Wins</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2">
          <p className="text-blue-400 font-medium">{quadrantCounts.majorProjects}</p>
          <p className="text-muted-foreground">Major Projects</p>
        </div>
        <div className="bg-secondary/50 border border-border rounded p-2">
          <p className="text-muted-foreground font-medium">{quadrantCounts.fillIns}</p>
          <p className="text-muted-foreground">Fill-Ins</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded p-2">
          <p className="text-red-400 font-medium">{quadrantCounts.avoid}</p>
          <p className="text-muted-foreground">Deprioritize</p>
        </div>
      </div>

      {/* Scatter Chart */}
      <div className="bg-secondary/30 rounded-lg p-4">
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, 100]}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 10 }}
              >
                <Label value="Effort Required" offset={-10} position="insideBottom" fill="#666" fontSize={12} />
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                domain={[0, 100]}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 10 }}
              >
                <Label value="Business Impact" angle={-90} position="insideLeft" fill="#666" fontSize={12} style={{ textAnchor: 'middle' }} />
              </YAxis>

              {/* Quadrant dividers */}
              <ReferenceLine x={50} stroke="#444" strokeDasharray="3 3" />
              <ReferenceLine y={50} stroke="#444" strokeDasharray="3 3" />

              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const item = payload[0].payload;
                  const quadrantLabel = getQuadrantLabel(item.x, item.y);
                  const quadrantColor = item.x < 50 && item.y >= 50 ? 'text-green-400'
                    : item.x >= 50 && item.y >= 50 ? 'text-blue-400'
                    : item.x < 50 && item.y < 50 ? 'text-gray-400'
                    : 'text-red-400';
                  return (
                    <div className="bg-background border border-border rounded-lg p-3 max-w-xs">
                      <p className="font-medium text-sm">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{item.category}</p>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Impact: {item.impact}</span>
                        <span>Effort: {item.effort}</span>
                      </div>
                      <p className={`text-xs font-medium mt-2 ${quadrantColor}`}>{quadrantLabel}</p>
                    </div>
                  );
                }}
              />

              <Scatter data={data} fill="#8884d8">
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getQuadrantColor(entry.x, entry.y)} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Quadrant Labels */}
        <div className="grid grid-cols-2 gap-4 mt-4 text-xs text-muted-foreground">
          <div className="text-right pr-4 border-r border-b border-border/30 pb-2">
            <span className="text-green-400 font-medium">Quick Wins</span>
            <p>High Impact, Low Effort</p>
            <p className="italic">Do these first!</p>
          </div>
          <div className="pl-4 border-b border-border/30 pb-2">
            <span className="text-blue-400 font-medium">Major Projects</span>
            <p>High Impact, High Effort</p>
            <p className="italic">Plan carefully</p>
          </div>
          <div className="text-right pr-4 border-r border-border/30 pt-2">
            <span className="font-medium">Fill-Ins</span>
            <p>Low Impact, Low Effort</p>
            <p className="italic">Do when time permits</p>
          </div>
          <div className="pl-4 pt-2">
            <span className="text-red-400 font-medium">Deprioritize</span>
            <p>Low Impact, High Effort</p>
            <p className="italic">Avoid or delegate</p>
          </div>
        </div>
      </div>
    </div>
  );
}
